/**
 * RelancePartner — section shown on the Détail page when status = INCOMPLET.
 *
 * Lets the user:
 *  - Pick a pre-defined WhatsApp template (multiple options)
 *  - Edit the message before copying
 *  - Copy to clipboard
 *  - Send to WhatsApp in 1 click (wa.me deep link, message pre-filled, partner number pre-resolved)
 *  - See history of past relances
 */
import { MissingVars } from "./SendForm";
import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Copy, Send, Bell, MessageSquare, ChevronDown, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const TONE_BADGES = {
  neutre: "bg-norea-surfaceHover text-norea-text border-norea-border",
  ferme: "bg-status-incompletBg text-status-incompletText border-status-incompletBorder",
  urgent: "bg-status-erreurBg text-status-erreurText border-status-erreurBorder",
  positif: "bg-status-completBg text-status-completText border-status-completBorder",
};

// Format a normalized E.164 digit-string (no '+') back for display:
//   "33612345678" -> "+33 6 12 34 56 78"  (FR)
//   other countries -> "+<digits>"        (best-effort)
function formatPhoneForDisplay(digits) {
  if (!digits) return "";
  const d = String(digits);
  if (d.startsWith("33") && d.length === 11) {
    const local = d.slice(2);
    return `+33 ${local.slice(0, 1)} ${local.slice(1, 3)} ${local.slice(3, 5)} ${local.slice(5, 7)} ${local.slice(7, 9)}`;
  }
  return `+${d}`;
}

const toIntl = (p) => {
  const d = String(p || "").replace(/\D/g, "");
  if (!d) return "";
  if (d.startsWith("33")) return d;
  return d.startsWith("0") ? `33${d.slice(1)}` : d;
};

export function withMissing(text, missing) {
  if (!missing?.length || missing.every((m) => text.includes(m))) return text;
  return `${text.trim()}\n\nPièces manquantes :\n${missing.map((m) => `- ${m}`).join("\n")}`;
}

export function clientMessage(dossier) {
  const ex = dossier.extraction || {};
  const who = [ex.civilite, ex.nom].filter(Boolean).join(" ") || ex.prenom || "";
  const missing = dossier.pieces_manquantes || [];
  return `Bonjour ${who},\n\nPour finaliser votre dossier de financement Norea, il nous manque les pièces suivantes :\n${missing.map((m) => `- ${m}`).join("\n")}\n\nVous pouvez nous les envoyer en photo ou en PDF directement en réponse à ce message.\n\nMerci et bonne journée,\nL'équipe Norea`;
}

export default function RelancePartner({ dossier, onUpdated, allowClient = false }) {
  const [target, setTarget] = useState("regie");
  const missing = dossier.pieces_manquantes || [];
  const [templates, setTemplates] = useState([]);
  const [clientTpl, setClientTpl] = useState(null);
  const [partner, setPartner] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/dossiers/_meta/whatsapp-templates`, {
          params: { dossier_id: dossier.id },
        });
        setTemplates(data.templates || []);
        setPartner(data.partner || null);
        setClientTpl(data.client || null);
        // Auto-select based on relance_count: 0 → template 1, 1 → template 2, 2+ → final
        const idx = Math.min(dossier.relance_count || 0, (data.templates || []).length - 1);
        const initial = data.templates?.[idx];
        if (initial) {
          setSelectedId(initial.id);
          setMessage(withMissing(initial.rendered || initial.body, allowClient ? missing : []));
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [dossier.id, dossier.relance_count]);

  const selectTemplate = (t) => {
    setSelectedId(t.id);
    setMessage(withMissing(t.rendered || t.body, allowClient ? missing : []));
  };
  const switchTarget = (tg) => {
    setTarget(tg);
    if (tg === "client") setMessage(clientTpl?.rendered || clientMessage(dossier));
    else {
      const t = templates.find((x) => x.id === selectedId);
      if (t) setMessage(withMissing(t.rendered || t.body, missing));
    }
  };

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(message);
      toast.success("📋 Message copié dans le presse-papier", { duration: 1500 });
    } catch {
      toast.error("Impossible de copier dans le presse-papier");
    }
  };

  const recordRelance = async (channel) => {
    // server-side audit trail; non-blocking for the WhatsApp open below
    try {
      const { data } = await api.post(`/dossiers/${dossier.id}/relance`, {
        template_id: target === "client" ? "client_pieces" : selectedId,
        message,
        channel: target === "client" ? `${channel}_client` : channel,
      });
      onUpdated?.({
        last_relance_at: data.last_relance_at,
        relance_count: data.relance_count,
      });
      return data;
    } catch {
      toast.error("Impossible d'enregistrer la relance");
      return null;
    }
  };

  const copyAndRecord = async () => {
    setBusy(true);
    try {
      await copyMessage();
      const data = await recordRelance("copy");
      if (data) {
        toast.success(`✓ Relance n°${data.relance_count} enregistrée`, {
          description: "Le message a été copié, collez-le dans WhatsApp.",
          duration: 3500,
        });
      }
    } finally {
      setBusy(false);
    }
  };

  const sendOnWhatsApp = async () => {
    if (!recipient?.whatsapp) return;
    setBusy(true);
    try {
      // Open wa.me in a new tab — pre-fills both the recipient number and the message.
      // The user only needs to press "Send" in WhatsApp.
      const url = `https://wa.me/${encodeURIComponent(recipient.whatsapp)}?text=${encodeURIComponent(message)}`;
      // Open BEFORE the async call to avoid popup blockers.
      window.open(url, "_blank", "noopener,noreferrer");
      const data = await recordRelance("whatsapp");
      if (data) {
        toast.success(`✓ Relance n°${data.relance_count} envoyée sur WhatsApp`, {
          description: `Destinataire : ${recipient.name} (${formatPhoneForDisplay(recipient.whatsapp)})`,
          duration: 4000,
        });
      }
    } finally {
      setBusy(false);
    }
  };

  const ex = dossier.extraction || {};
  const recipient = target === "client"
    ? { name: [ex.prenom, ex.nom].filter(Boolean).join(" ") || "Client", whatsapp: toIntl(ex.telephone || ex.telephone_2) }
    : partner;
  const hasWhatsApp = !!recipient?.whatsapp;

  return (
    <div className="card-flat p-5" data-testid="relance-card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="overline">{target === "client" ? "Relance client" : "Relance partenaire"}</div>
          <h3 className="font-heading text-lg font-medium text-norea-text mt-1 flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-norea-accent" />
            Message WhatsApp
          </h3>
          <p className="text-xs text-norea-textMuted mt-1">
            {target === "client" ? "Message pré-rempli avec les pièces manquantes, ajustez si besoin puis envoyez sur WhatsApp." : "Choisissez un modèle, ajustez si besoin, puis envoyez en 1 clic sur WhatsApp."}
          </p>
        </div>
        {(dossier.relance_count || 0) > 0 && (
          <div className="text-right">
            <div className="inline-flex items-center gap-1.5 text-xs bg-status-incompletBg border border-status-incompletBorder text-status-incompletText px-2.5 py-1 rounded-md">
              <Bell className="w-3.5 h-3.5" />
              <span className="font-medium tabular-nums">{dossier.relance_count}</span>
              <span>relance{dossier.relance_count > 1 ? "s" : ""}</span>
            </div>
            {dossier.last_relance_at && (
              <div className="text-[11px] text-norea-textMuted mt-1">
                Dernière : {new Date(dossier.last_relance_at).toLocaleString("fr-FR")}
              </div>
            )}
          </div>
        )}
      </div>

      {allowClient && (
        <div className="mb-3 inline-flex rounded-md border border-norea-border p-0.5 bg-norea-bg" data-testid="relance-target">
          {[["regie", "Régie"], ["client", "Client"]].map(([k, l]) => (
            <button key={k} onClick={() => switchTarget(k)} className={`px-3 py-1 text-xs font-medium rounded ${target === k ? "bg-white shadow-sm text-norea-text" : "text-norea-textMuted"}`} data-testid={`relance-target-${k}`}>{l}</button>
          ))}
        </div>
      )}
      {/* Partner contact bar — shows who will receive the relance */}
      {!loading && recipient && (
        <div
          className={`mb-3 px-3 py-2 rounded-md border text-xs flex items-center justify-between gap-3 ${
            hasWhatsApp
              ? "bg-status-completBg/40 border-status-completBorder text-norea-text"
              : "bg-status-incompletBg border-status-incompletBorder text-status-incompletText"
          }`}
          data-testid="relance-partner-bar"
        >
          <div className="flex items-center gap-2 min-w-0">
            <span className="overline">Destinataire</span>
            <span className="font-medium truncate">{recipient.name || dossier.partenaire || "—"}</span>
            {hasWhatsApp ? (
              <span className="font-mono tabular-nums text-norea-textMuted" data-testid="relance-partner-number">
                {formatPhoneForDisplay(recipient.whatsapp)}
              </span>
            ) : (
              <span className="italic">— numéro WhatsApp non configuré</span>
            )}
          </div>
          {!hasWhatsApp && target === "regie" && (
            <RouterLink
              to="/configuration"
              className="inline-flex items-center gap-1 font-medium hover:underline shrink-0"
              data-testid="relance-add-number-link"
            >
              Ajouter <ExternalLink className="w-3 h-3" />
            </RouterLink>
          )}
        </div>
      )}

      {loading ? (
        <div className="h-32 bg-norea-bg rounded animate-pulse" />
      ) : (
        <>
          <div className="mb-3"><MissingVars missing={target === "client" ? clientTpl?.missing : templates.find((t) => t.id === selectedId)?.missing} testid="relance-missing-vars" /></div>
          {/* Template chooser */}
          <div className={`flex flex-wrap gap-2 mb-3 ${target === "client" ? "hidden" : ""}`} data-testid="relance-templates">
            {templates.map((t) => (
              <button
                key={t.id}
                onClick={() => selectTemplate(t)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border transition ${
                  selectedId === t.id
                    ? "bg-norea-primary text-white border-norea-primary"
                    : "bg-white text-norea-text border-norea-border hover:bg-norea-surfaceHover"
                }`}
                data-testid={`tpl-${t.id}`}
              >
                <span>{t.label}</span>
                {selectedId !== t.id && (
                  <span className={`inline-flex items-center px-1 py-0.5 text-[9px] uppercase tracking-wider font-semibold rounded border ${TONE_BADGES[t.tone] || TONE_BADGES.neutre}`}>
                    {t.tone}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Editable message */}
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={10}
            className="input-field font-mono text-xs leading-relaxed resize-y"
            placeholder="Tapez votre message ou choisissez un modèle ci-dessus…"
            data-testid="relance-textarea"
          />

          {/* Actions */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {/* Primary action — WhatsApp 1-click */}
            <button
              onClick={sendOnWhatsApp}
              disabled={busy || !message.trim() || !hasWhatsApp}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-[#25D366] hover:bg-[#1ebe57] disabled:opacity-40 disabled:cursor-not-allowed transition"
              data-testid="btn-relance-whatsapp"
              title={hasWhatsApp ? `Envoyer sur WhatsApp à ${formatPhoneForDisplay(recipient.whatsapp)}` : "Ajoutez d'abord un numéro WhatsApp dans /configuration"}
            >
              <WhatsAppIcon className="w-4 h-4" />
              {busy ? "Ouverture…" : "Envoyer sur WhatsApp"}
            </button>
            <button onClick={copyMessage} className="btn-secondary" data-testid="btn-relance-copy">
              <Copy className="w-4 h-4" />
              Copier
            </button>
            <button
              onClick={copyAndRecord}
              disabled={busy || !message.trim()}
              className="btn-ghost text-xs"
              data-testid="btn-relance-record"
              title="Copier le message et enregistrer la relance sans ouvrir WhatsApp"
            >
              <Send className="w-3.5 h-3.5" />
              Copier + marquer relancé
            </button>
          </div>

          {/* History */}
          {dossier.relance_history?.length > 0 && (
            <details className="mt-4" data-testid="relance-history">
              <summary className="cursor-pointer text-xs text-norea-textMuted hover:text-norea-text inline-flex items-center gap-1">
                <ChevronDown className="w-3 h-3" />
                Historique des {dossier.relance_history.length} relance(s)
              </summary>
              <ol className="mt-2 space-y-1.5 text-xs">
                {[...dossier.relance_history].reverse().map((h, i) => (
                  <li key={i} className="flex items-center gap-2 px-3 py-1.5 bg-norea-bg rounded border border-norea-border">
                    <Bell className="w-3 h-3 text-norea-textMuted" />
                    <span className="font-mono tabular-nums text-norea-textMuted">
                      {new Date(h.at).toLocaleString("fr-FR")}
                    </span>
                    <span>·</span>
                    <span className="text-norea-text">{h.by}</span>
                    {h.template_id && (
                      <span className="text-norea-textMuted">via {h.template_id}</span>
                    )}
                    {h.channel && (
                      <span className="text-norea-textMuted">· {h.channel}</span>
                    )}
                  </li>
                ))}
              </ol>
            </details>
          )}
        </>
      )}
    </div>
  );
}

/** Inline WhatsApp SVG glyph (no extra dep). */
function WhatsAppIcon({ className }) {
  return (
    <svg viewBox="0 0 32 32" className={className} fill="currentColor" aria-hidden="true">
      <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.506 3.41 4.554 4.34.616.287 2.035.888 2.722.888.817 0 2.392-.387 2.764-1.16.13-.272.187-.5.187-.76 0-.5-.013-.5-.187-.6-.087-.044-2.665-1.49-3.182-1.49z"/>
      <path d="M27.04 4.937C24.092 1.99 20.18.37 16.014.37c-8.62 0-15.626 7.005-15.626 15.626 0 2.755.715 5.45 2.082 7.83L.296 31.66l7.984-2.094a15.6 15.6 0 0 0 7.69 1.99h.006c8.625 0 15.732-7.005 15.732-15.626 0-4.176-1.74-8.09-4.668-11.036zM16.014 28.92h-.005a13 13 0 0 1-6.62-1.81l-.476-.282-4.74 1.245 1.265-4.622-.31-.49a12.974 12.974 0 0 1-1.992-6.94c0-7.16 5.83-12.987 13-12.987 3.47 0 6.73 1.352 9.176 3.81a12.86 12.86 0 0 1 3.798 9.183c0 7.162-5.83 12.992-12.997 12.992z"/>
    </svg>
  );
}
